import type { ReferenceGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { assembleInfoMorphemeSliceDefinitionList } from "#assemble-info-morpheme-slice-definition-list";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import { hasLength, hasMinLength } from "@-/com.npmjs.ts-array-length";

function toMorphemeTier(tier: number | null | undefined): ReferenceGraphemeMorphemeSlice["tier"] {
  if (tier === 1 || tier === 2 || tier === 3) {
    return tier;
  }
  return null;
}

function coerceMedianPolyline(
  median: [number, number][] | null | undefined,
): NonNullable<ReferenceGraphemeMorphemeSlice["graphemeStrokes"]>[number]["median"] {
  if (median === null || median === undefined) {
    return [];
  }
  const out: NonNullable<ReferenceGraphemeMorphemeSlice["graphemeStrokes"]>[number]["median"] = [];
  for (const tuple of median) {
    if (!Array.isArray(tuple) || !hasMinLength(tuple, 2)) {
      continue;
    }
    const x = Math.trunc(Number(tuple[0]));
    const y = Math.trunc(Number(tuple[1]));
    if (Number.isFinite(x) && Number.isFinite(y)) {
      out.push([x, y]);
    }
  }
  return out;
}

export function useGetReferenceGraphemeMorphemeSliceQueryKey() {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  return (grapheme: string) => ["infoGraphemeMorphemeSlice", grapheme, { staticDb }] as const;
}

export const [useInfoGraphemeMorphemeSliceQueryOptions, infoGraphemeMorphemeSliceQueryFn] =
  createUseQueryOptions(
    useGetReferenceGraphemeMorphemeSliceQueryKey,
    async ({ queryKey: [_, grapheme, { staticDb }] }) => {
      // Each grapheme together with everything the info back needs: its
      // definitions, decomposition components, PRC level/tier data, frequency
      // ranks, and ordered similar graphemes (with their glyphs) — every grapheme
      // sharing the same decomposition. The info data is taken from the first
      // matching grapheme; strokes span all rows sharing the glyph.
      const db = staticDb(import.meta.location);
      const graphemeRows = await db.query.grapheme.findMany({
        columns: { glyph: true, id: true, ids: true, strokeCount: true },
        where: { glyph: grapheme },
        with: {
          biau: { columns: { rank: true } },
          components: {
            columns: { componentGraphemeId: true },
            orderBy: { id: "asc" },
          },
          definitions: { columns: { definition: true, pinyin: true } },
          prcData: {
            columns: {
              minHsk2010Level: true,
              minHsk2021Level: true,
              minHsk2026Level: true,
              tier: true,
            },
          },
          similarGraphemes: {
            columns: { similarGraphemeId: true },
            orderBy: { index: "asc" },
            with: { similarGrapheme: { columns: { glyph: true } } },
          },
          strokeData: { columns: { strokeData: true } },
          subtlex: { columns: { rank: true } },
        },
      });
      const matchedGrapheme = graphemeRows[0];
      if (matchedGrapheme === undefined) {
        return null;
      }

      const definitionRows = matchedGrapheme.definitions;
      const definitionList = assembleInfoMorphemeSliceDefinitionList(definitionRows);
      const prcData = matchedGrapheme.prcData;
      // Info composition follows Wiktionary IDS. `grapheme_component` may instead carry a
      // curator-pinned study override, so it is only the fallback when info IDS is unavailable.
      const componentGraphemeIdList =
        matchedGrapheme.ids === null
          ? matchedGrapheme.components.map((component) => component.componentGraphemeId)
          : (([, ...componentIds]) => componentIds)(matchedGrapheme.ids);
      const componentGraphemeRows = hasLength(componentGraphemeIdList, 0)
        ? []
        : await db.query.grapheme.findMany({
            columns: { glyph: true, id: true },
            where: { id: { in: uniq(componentGraphemeIdList) } },
            with: {
              definitions: { columns: { pinyin: true } },
            },
          });
      const componentById = new Map(
        componentGraphemeRows.map((component) => [
          component.id,
          {
            grapheme: component.glyph,
            pinyinList: uniq(component.definitions.map((definition) => definition.pinyin)),
          },
        ]),
      );
      // Keep authored IDS order and repeated occurrences (e.g. 二 → 一 + 一). The info slice
      // carries the display-ready glyph/pinyin data so the UI never has to resolve static numeric IDs
      // through the separately bundled content database.
      const components = componentGraphemeIdList.flatMap((componentId) => {
        const component = componentById.get(componentId);
        return component === undefined ? [] : [component];
      });

      const seenSimilarGraphemeIds = new Set<number>();
      const similarGraphemeList: string[] = [];
      for (const row of matchedGrapheme.similarGraphemes) {
        if (seenSimilarGraphemeIds.has(row.similarGraphemeId)) {
          continue;
        }
        seenSimilarGraphemeIds.add(row.similarGraphemeId);
        similarGraphemeList.push(row.similarGrapheme.glyph);
      }

      // `null` when any glyph has no stroke data (see `graphemeStrokeListSchema`).
      let graphemeStrokes: ReferenceGraphemeMorphemeSlice["graphemeStrokes"] = [];
      let strokeIndex = 0;
      for (const g of graphemeRows) {
        const strokeData = g.strokeData?.strokeData ?? null;
        if (strokeData === null) {
          graphemeStrokes = null;
          break;
        }
        for (const { medians, path } of strokeData) {
          graphemeStrokes.push({
            index: strokeIndex,
            median: coerceMedianPolyline(medians),
            path,
          });
          strokeIndex += 1;
        }
      }

      const pinyinList: ReferenceGraphemeMorphemeSlice["pinyinList"] = uniq(
        definitionRows.map((row) => row.pinyin),
      ).map((pinyin) => ({
        gloss: null,
        mnemonic: null,
        toneNumbersPinyin: pinyin,
      }));

      return {
        biau1Rank: matchedGrapheme.biau?.rank ?? null,
        components,
        definitionList,
        grapheme: matchedGrapheme.glyph,
        graphemeId: matchedGrapheme.id,
        graphemeStrokes,
        hsk2010Level: prcData?.minHsk2010Level ?? null,
        hsk2021Level: prcData?.minHsk2021Level ?? null,
        hsk2026Level: prcData?.minHsk2026Level ?? null,
        morphemeType: "grapheme" as const,
        pinyinList,
        similarGraphemeList,
        subtlexChrRank: matchedGrapheme.subtlex?.rank ?? null,
        tier: toMorphemeTier(prcData?.tier),
      };
    },
  );

// Reads only the static DB (bundled / read-only, immutable at runtime) and the key carries no
// change-version, so a resolved slice never goes stale. Freshness is unnecessary here — the global
// QueryClient default `staleTime: Infinity` lets a remount (e.g. a lazy tab re-mounting after an
// ancestor suspends) resolve synchronously from cache instead of re-issuing the relational read, so
// no per-query `staleTime` is needed.
